import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { ContentContext } from '../../../context/ContentProvider';
import { PAYWALL_COLUMNS } from '../../../../app_constant';

const PremiumBanner = ({ theme, onViewPlans }) => {
  const { examStats, totals } = useContext(ContentContext);

  const styles = {
    container: {
      background: theme?.background?.card,
      borderRadius: '1.5rem',
      padding: '1.5rem',
      width: '100%',
      boxShadow: theme?.shadows?.xl,
      border: `1px solid ${theme?.borders?.primary}`,
      position: 'relative',
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      textAlign: 'center',
      marginBottom: '0.5rem',
    },
    subtitle: {
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
      textAlign: 'center',
      marginBottom: '1.25rem',
      lineHeight: '1.5',
    },
    features: {
      marginBottom: '1.25rem',
    },
    feature: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '0.75rem',
      marginBottom: '0.625rem',
      fontSize: '0.875rem',
      color: theme?.text?.primary,
      lineHeight: '1.5',
    },
    checkIcon: {
      color: theme?.primary?.green?.[500],
      fontSize: '0.875rem',
      marginTop: '0.125rem',
      flexShrink: 0,
    },
    highlightedText: {
      color: theme?.primary?.blue?.[500] || '#0973ba',
      fontWeight: '600',
    },
    button: {
      width: '100%',
      padding: '0.875rem 1.5rem',
      borderRadius: '0.75rem',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      background: `linear-gradient(135deg, ${theme?.primary?.orange?.[500]}, ${theme?.primary?.orange?.[600]})`,
      color: 'white',
    },
    buttonHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    },
  };

  return (
    <motion.div
      style={styles.container}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <h2 style={styles.title}>Unlock Unlimited Access</h2>

      <p style={styles.subtitle}>
        Upgrade to unlock unlimited access and accelerate your exam preparation.
      </p>

      <div style={styles.features}>
        {/* Feature 1: Unlimited access to exams */}
        <motion.div
          style={styles.feature}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0 }}
        >
          <FontAwesomeIcon icon={faCheckCircle} style={styles.checkIcon} />
          <span>
            Unlimited access to{' '}
            {examStats && examStats.length > 0 ? (
              examStats.length === 1 ? (
                <>
                  <span style={styles.highlightedText}>{examStats[0].title}</span>
                  {' exam prep'}
                </>
              ) : examStats.length === 2 ? (
                <>
                  <span style={styles.highlightedText}>{examStats[0].title}</span>
                  {' and '}
                  <span style={styles.highlightedText}>{examStats[1].title}</span>
                  {' exam prep'}
                </>
              ) : (
                <>
                  {examStats.map((exam, index) => (
                    <React.Fragment key={exam.key}>
                      <span style={styles.highlightedText}>{exam.title}</span>
                      {index < examStats.length - 2 && ', '}
                      {index === examStats.length - 2 && ', and '}
                    </React.Fragment>
                  ))}
                  {' exam prep'}
                </>
              )
            ) : (
              <span style={styles.highlightedText}>exam prep</span>
            )}
          </span>
        </motion.div>

        {/* Feature 2: Practice questions */}
        <motion.div
          style={styles.feature}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <FontAwesomeIcon icon={faCheckCircle} style={styles.checkIcon} />
          <span>
            <span style={styles.highlightedText}>
              {totals?.questions ? `${totals.questions.toLocaleString()}` : '1,000+'}
            </span>{' '}
            practice questions with detailed explanations
          </span>
        </motion.div>

        {/* Feature 3: Additional content (configurable via PAYWALL_COLUMNS) */}
        <motion.div
          style={styles.feature}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <FontAwesomeIcon icon={faCheckCircle} style={styles.checkIcon} />
          {PAYWALL_COLUMNS ? (
            <span>
              {PAYWALL_COLUMNS.filter(col => col.key !== 'questions').map((col, index, arr) => (
                <React.Fragment key={col.key}>
                  {index > 0 && (index === arr.length - 1 ? ' and ' : ', ')}
                  <span style={styles.highlightedText}>
                    {totals?.[col.key] ? totals[col.key].toLocaleString() : ''}
                  </span>{' '}
                  {col.label.charAt(0) + col.label.slice(1).toLowerCase()}
                </React.Fragment>
              ))}
              {' '}across all exams
            </span>
          ) : totals?.flashcards > 0 || totals?.articles > 0 ? (
            <span>
              <span style={styles.highlightedText}>
                {totals.flashcards.toLocaleString()}
              </span>{' '}
              flashcards and{' '}
              <span style={styles.highlightedText}>
                {totals.articles.toLocaleString()}
              </span>{' '}
              study articles
            </span>
          ) : (
            <span>
              <span style={styles.highlightedText}>
                {totals?.practiceTests ? totals.practiceTests.toLocaleString() : '36'}
              </span>{' '}
              Practice Tests across all exams
            </span>
          )}
        </motion.div>

        {/* Feature 4: Readiness Score */}
        <motion.div
          style={styles.feature}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
        >
          <FontAwesomeIcon icon={faCheckCircle} style={styles.checkIcon} />
          <span>
            Track exam readiness with{' '}
            <span style={styles.highlightedText}>Readiness Score</span>
          </span>
        </motion.div>
      </div>

      <motion.button
        style={styles.button}
        whileHover={styles.buttonHover}
        whileTap={{ scale: 0.95 }}
        onClick={onViewPlans}
      >
        <FontAwesomeIcon icon={faCrown} />
        View Plans
      </motion.button>
    </motion.div>
  );
};

export default PremiumBanner;
